import { useCallback, useMemo, useRef } from 'react';
import useApiQueries from 'lib/api/useApiQueries';

/**
 * runs one query per selected agency and folds the per-agency states into what the
 * appointments center widgets need. every agency keeps its own cache slot, retry
 * budget and error state, which is what lets Retry refetch only the ones that failed
 * instead of throwing away the agencies that loaded fine.
 * @param agencies - list of { agencyId, agencyName }
 * @param buildEntry - (agency) => { queryKey, request, parse }; must be stable, so
 *   declare it at module level or wrap it in useCallback
 * @param options - useQuery options shared by every agency's query
 */
export default function useAgencyFanOutQueries(
  agencies,
  buildEntry,
  options = {}
) {
  const safeAgencies = useMemo(
    () => (Array.isArray(agencies) ? agencies : []),
    [agencies]
  );

  const entries = useMemo(
    () => safeAgencies.map(buildEntry),
    [safeAgencies, buildEntry]
  );

  const queries = useApiQueries(entries, options);

  // useQueries hands back a fresh array every render, so nothing downstream can
  // depend on it directly. these signatures change only when an agency actually
  // resolved or failed, which is what keeps the derived lists referentially stable.
  const dataSignature = queries.map(q => q.dataUpdatedAt).join(',');
  const errorSignature = queries.map(q => (q.isError ? '1' : '0')).join('');

  const queriesRef = useRef(queries);
  queriesRef.current = queries;

  const results = useMemo(
    () =>
      safeAgencies.map((agency, i) => ({
        ...agency,
        data: queriesRef.current[i]?.data,
        isError: Boolean(queriesRef.current[i]?.isError)
      })),
    [safeAgencies, dataSignature, errorSignature]
  );

  const failedAgencies = useMemo(
    () =>
      safeAgencies
        .filter((_, i) => errorSignature[i] === '1')
        .map(({ agencyId, agencyName }) => ({ agencyId, agencyName })),
    [safeAgencies, errorSignature]
  );

  const retryFailed = useCallback(
    () =>
      Promise.all(
        queriesRef.current.filter(q => q.isError).map(q => q.refetch())
      ),
    []
  );

  // full refresh for callers that changed something server-side and need every
  // agency re-read, not just the ones that failed
  const refetchAll = useCallback(
    () => Promise.all(queriesRef.current.map(q => q.refetch())),
    []
  );

  return {
    results,
    failedAgencies,
    retryFailed,
    refetchAll,
    // first load still in flight for at least one agency: nothing to show yet
    isLoading: queries.some(q => q.isLoading),
    // a fetch with data already on screen: revalidating or retrying
    isFetching: queries.some(q => q.isFetching)
  };
}
